import {
  DynamicComponentTypes,
  DynamicLPContentBlock,
  DynamicLPDataObject,
  InterlinkingDestinationsMenu,
  PeaceOfMindSection,
  BrochureSection as BrochureSectionType,
  SponsorImages,
  DynamicLPModule,
  RelatedBlogsSection,
  TermsAndConditionsSection,
  OfferSavingsTable,
  CountdownTimerSection
} from "@/types/dto/landing-page/landing-page";
import { FeefoSummary } from "@/types/dto/common/feefo-reviews";
import { Tour } from "@/types/dto/tours/tour";
import {
  ContactFormSection,
  CrowdRiffSection,
  FAQSection,
  TourCarousel,
  TourHighlightsSection,
  UnforgettableMoments
} from "@/types/dto/destinations-page/destination-page-types";
import { CardGrid, Reviews, MediaCarousel } from "@/types/dto/homepage/homepage-types";
import { TravelStyleSection } from "@/types/dto/deals-page/deals-page-types";
import { ContentPanelImageTextSection, MediaCard } from "@/types/dto/common/media-card-section";
import { FlexibleCardLayoutSection } from "@/types/dto/flexible-card-layout-section";
import { Hero } from "@/types/dto/hero/hero";
import { HubspotFormShellModule } from "@/types/hubspot/hubspot-form";
import { BasicInfoMultipleCtas } from "@/types/dto/common/basic-info";
import offerSavingsTableFactory from "@/factories/landing-page/offer-savings-table-factory";
import {alpha, Theme} from "@mui/material";
import {
  isDarkLandingPageBackground,
  resolveLandingPageSectionBackgroundColor,
  shouldUseLandingPagePatternBackground
} from "@/utils/landing-page-background";

export interface DynamicFactoryContext {
  openRequestAQuote?: (tour: Tour) => void;
}

export interface DynamicLPModuleEntry {
  id: string;
  type: DynamicComponentTypes;
  props: DynamicLPModule;
}

type ModuleResolver = (data: DynamicLPDataObject, context: DynamicFactoryContext) => unknown;

const factoryMap: Record<DynamicComponentTypes, ModuleResolver> = {
  heroSplitLayoutTitleSubtitleImageCtAs: (data) => {
    const hero = data as Hero;
    return {
      heroBasicInfo: hero.basicInfo,
      headerImage: hero.backgroundImage,
      video: hero.video,
      feefoSummary: hero.feefoSummary,
      testId: "landing-page-hero",
      padding: { mobile: "4rem 2.4rem", desktop: "6rem 10rem" }
    };
  },
  heroSplitLayoutTitleSubtitleFeefoRating: (data) => {
    const hero = data as Hero;
    return {
      heroBasicInfo: hero.basicInfo,
      headerImage: hero.backgroundImage,
      video: hero.video,
      feefoSummary: hero.feefoSummary,
      testId: "landing-page-hero",
      isSmFeefoSummary: true,
      padding: { mobile: "4rem 2.4rem", desktop: "6rem 10rem" }
    };
  },
  heroBackgroundImageTitleSubtitleCtAs: (data) => {
    const hero = data as Hero;
    return {
      imageSrc: hero.backgroundImage.imageUrl,
      imageAlt: hero.backgroundImage.altText,
      basicInfoMultipleCtas: hero.basicInfo,
      logoImage: hero.logoImage,
      variant: hero.logoImage ? "logo" : "text",
      dataParentContainerType: "Landing Page",
      dataModuleName: "Landing Page Hero",
      pageTitle: hero.basicInfo.headerText
    };
  },
  heroBackgroundImageLockUp: (data) => {
    const hero = data as Hero;
    return {
      imageSrc: hero.backgroundImage.imageUrl,
      imageAlt: hero.backgroundImage.altText,
      basicInfoMultipleCtas: hero.basicInfo,
      logoImage: hero.logoImage,
      variant: "logo",
      dataParentContainerType: "Landing Page",
      dataModuleName: "Landing Page Hero",
      pageTitle: hero.basicInfo.headerText,
      locationText: hero.locationText,
      disclaimerText: hero.disclaimerText,
      slotProps: {
        backgroundImage: {
          sx: {
            background: (theme: Theme) => `linear-gradient(0deg, ${alpha(theme.palette.common.black, 0.4)} 0%, ${alpha(theme.palette.common.black, 0)} 100%)`
          }
        }
      }
    };
  },
  feefoBar: (data) => ({
    summary: data as FeefoSummary
  }),
  navigationMenuImageCardGrid: (data) => ({
    destinations: data as InterlinkingDestinationsMenu
  }),
  carouselToursManuallySelected: (data, context) => {
    const featuredTourCarousel = data as TourCarousel;
    return {
      openRequestAQuote: context.openRequestAQuote,
      tourCarousel: {
        basicInfo: featuredTourCarousel.basicInfo,
        tours: featuredTourCarousel.tours,
        anchorId: featuredTourCarousel.anchorId
      },
      padding: {
        mobile: "4rem 2.4rem",
        tablet: "4rem 2.4rem",
        desktop: "6rem 10rem",
        wide: "6rem 10rem"
      },
      parentContainerType: "Landing Page",
      showRating: true
    };
  },
  valuePropsTitleSubtitleIconCards: (data) => {
    const peaceOfMindData = data as PeaceOfMindSection;
    return {
      testId: "landing-page-hero",
      sectionInfo: peaceOfMindData.basicInfo,
      icons: peaceOfMindData.icons,
      theme: peaceOfMindData.theme
    };
  },
  brochureFeatureCtAsImage: (data) => ({
    ...(data as BrochureSectionType),
    variant: "modalForm"
  }),
  brochureFeatureInlineViewOnlineFormImage: (data) => ({
    ...(data as BrochureSectionType),
    variant: "embeddedForm"
  }),
  itineraryCardGridImageTitleTourCtAs: (data) => ({
    moments: data as UnforgettableMoments,
    backgroundColor: (data as UnforgettableMoments).backgroundColor,
    containerSx: {
      backgroundColor: resolveLandingPageSectionBackgroundColor(
        (data as UnforgettableMoments).backgroundColor,
        "transparent"
      ),
      padding: { xs: "4rem 2.4rem", lg: "6rem 10rem" }
    }
  }),
  faqAccordion: (data) => ({
    faqSection: data as FAQSection,
    faqContainerSx: {
      padding: { xs: "4rem 2.4rem", lg: "6rem 10rem" }
    },
    headerSx: {
      textTransform: "none"
    }
  }),
  galleryCrowdRiffFeed: (data) => ({
    crowdRiffSection: data as CrowdRiffSection,
    showCopyText: true,
    sectionHeaderCentered: true,
    containerSx: {
      padding: { xs: "4rem 2.4rem", lg: "6rem 10rem" },
      maxWidth: "144rem",
      width: "100%",
      margin: "auto"
    },
    headerSx: {
      textTransform: "none"
    }
  }),
  imageGridMultiColumn: (data) => {
    const imageGridData = data as SponsorImages | {
      logos: SponsorImages;
      logoGridHeader?: string;
    };
    const logos = Array.isArray(imageGridData)
      ? imageGridData
      : imageGridData.logos;

    return {
      logos,
      ...(!Array.isArray(imageGridData) && imageGridData.logoGridHeader
        ? { logoGridHeader: imageGridData.logoGridHeader }
        : {}),
      moduleName: "Sponsor Images",
      parentContainerType: "sponsor-logos"
    };
  },
  reviewsManualEntry3Column: (data) => ({
    reviewsData: data as Reviews,
    feefoSummary: (data as Reviews).feefoSummary
  }),
  ctaFullWidthCardTitleSubtitleCta: (data) => ({
    ...(data as BrochureSectionType)
  }),
  campaignCarouselMarketingCardsImageTitleCta: (data) => ({
    campaignSection: data as CardGrid
  }),
  featuredCategoriesCardGridImageDescriptionCta: (data) => ({
    data: data as TravelStyleSection
  }),
  contentPanelImageText: (data) => {
    const contentPanelData = data as ContentPanelImageTextSection;
    const card = contentPanelData.mediaCardWithText;

    return {
      card,
      ctas: contentPanelData.ctas,
      sectionBackgroundColor: contentPanelData.backgroundColor,
      showImageText: contentPanelData.showImageText ?? false,
      reverseDesktop: card?.design === "Text Left",
      reverseMobile: card?.design === "Text Left"
    };
  },
  carouselOrZigZagImageCta: (data) => ({
    section: data as FlexibleCardLayoutSection
  }),
  tourCarouselFeaturedToursNoPricingImageCTAs: (data) => ({
    tourHighlightsSection: data as TourHighlightsSection
  }),
  videoCarouselWistiaVideos: (data) => ({
    ...(data as MediaCarousel)
  }),
  videoEmbedWistiaCenteredSection: (data) => ({
    mediaCardWithText: data as MediaCard
  }),
  contactFormTwoColumnSupportingContent: (data) => ({
    contactFormSection: data as ContactFormSection
  }),
  leadCaptureFormCenteredPanelHeaderForm: (data) => ({
    ...(data as HubspotFormShellModule)
  }),
  textModuleHeaderCopyCtAs: (data) => {
    const salesContent = data as BasicInfoMultipleCtas;
    const isPatternBackground = shouldUseLandingPagePatternBackground(
      salesContent.backgroundColor,
      true
    );
    const resolvedBackgroundColor = resolveLandingPageSectionBackgroundColor(
      salesContent.backgroundColor,
      "transparent"
    );
    const textColor = isDarkLandingPageBackground(salesContent.backgroundColor)
      ? "primary.contrastText"
      : "text.primary";

    return {
      basicInfo: salesContent,
      slotProps: {
        root: {
          className: isPatternBackground ? "pattern-bg" : undefined,
          sx: isPatternBackground
            ? undefined
            : {
              backgroundColor: resolvedBackgroundColor
            }
        },
        container: {
          sx: {
            paddingBottom: "3.2rem !important"
          }
        },
        ctaContainer: {
          sx: {
            display: "flex",
            paddingTop: "2rem",
            justifyContent: "center",
            alignItems: "center",
            gap: "2.4rem",
            padding: "2rem",
            flexDirection: { xs: "column", md: "row" },
            flexWrap: "wrap"
          }
        },
        copy: {
          sx: {
            color: textColor,
            "& p": {
              marginBottom: "1.6rem",
              "&:last-child": {
                marginBottom: 0
              }
            }
          }
        },
        header: {
          sx: {
            color: textColor
          }
        }
      }
    };
  },
  textModuleTermsConditions: (data) => ({
    copyText: (data as TermsAndConditionsSection).termsConditions,
    backgroundColor: (data as TermsAndConditionsSection).backgroundColor,
    dataParentContainer: "dynamic-landing-page",
    dataModuleName: "terms and conditions"
  }),
  relatedBlogsSection: (data) => ({
    blogSection: data as RelatedBlogsSection
  }),
  offerSavingsTable: (data) => {
    const offerSavingsTable = offerSavingsTableFactory(data as OfferSavingsTable);
    return offerSavingsTable ? { offerSavingsTable } : undefined;
  },
  countdownTimer: (data) => ({
    countdownTimer: data as CountdownTimerSection
  })
};

const dynamicLandingPageModulesFactory = (
  contentBlocks: DynamicLPContentBlock[],
  context: DynamicFactoryContext
): DynamicLPModuleEntry[] => {
  const typeCountMap: Partial<Record<string, number>> = {};

  return contentBlocks.reduce<DynamicLPModuleEntry[]>((acc, { type, data }) => {
    const resolver = factoryMap[type];
    if (resolver) {
      const count = typeCountMap[type] ?? 0;
      typeCountMap[type] = count + 1;
      try {
        const props = resolver(data, context) as DynamicLPModule | undefined;
        if (props) {
          acc.push({ id: `${type}-${count}`, type, props });
        }
      } catch {
        // Skip malformed CMS modules so the rest of the page can render.
      }
    }
    return acc;
  }, []);
};

export default dynamicLandingPageModulesFactory;
